import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  CircularProgress,
  Grid,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useState } from "react";
import { CustomEvent } from "schemas/functions";

import QuestionChip from "components/Styled/QuestionChip";
import { fetcherAuth } from "utils/api";
import { normalizePhone } from "utils/form";
import { makeDeepCopy } from "utils/updateLocalState";

export default function UserNotificationsSettings() {
  const { getToken } = useAuth();
  const { setAlert } = useContext(AlertContext);
  const [loading, setLoading] = useState(false);
  const {
    currentOrg,
    currentUser,
    setUserNotifications,
    userNotifications,
    profile,
    setProfile,
  } = useContext(OrganizationUserContext);

  const [doNotEmailLoading, setDoNotEmailLoading] = useState(false);
  const [doNotEmail, setDoNotEmail] = useState(
    currentUser?.doNotEmail || false,
  );

  const handleChangePhone = (e: CustomEvent) => {
    const { value } = e.target;
    const copy = makeDeepCopy(profile);
    copy.phoneNumber = value;
    setProfile(copy);
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      let data = {
        ...userNotifications,
        ...profile,
      };

      const response = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/settings`,
        "PUT",
        {},
        data,
      );
      setProfile(response.organizationProfile);
      setAlert("Profile saved", "success");
    } catch (error) {
      setAlert(
        error.message || "An error occured when updating profile",
        "warning",
      );
    } finally {
      setLoading(false);
    }
  };

  const updateDoNotEmail = async (value: boolean) => {
    setDoNotEmailLoading(true);
    await fetcherAuth(
      getToken,
      `/api/organization/${currentOrg?.id}/users`,
      "PATCH",
      {},
      { doNotEmail: value },
    );
    setDoNotEmailLoading(false);
  };

  const handleDoNotEmailToggle = async (checked: boolean) => {
    setDoNotEmail(checked);
    try {
      await updateDoNotEmail(checked);
      setAlert("Your email preferences have been updated", "success");
    } catch (error) {
      setDoNotEmail(!checked); // revert on failure
      setAlert(error.message || "An error occurred", "warning");
    }
  };

  const handleSelect = (selectedOption: string) => {
    setUserNotifications((prev) => {
      if (!prev) {
        prev = {};
      }
      const copy = makeDeepCopy(prev);
      copy[selectedOption] = !copy[selectedOption];
      return copy;
    });
  };

  return (
    <>
      <Box sx={{ mb: 3 }}>
        <Typography gutterBottom>
          How would you like to receive notifications and updates about your
          account?
        </Typography>

        <Typography variant="caption" color="textSecondary" paragraph>
          Select all that apply.
        </Typography>

        <Grid container gap={1} alignItems={"center"}>
          <QuestionChip
            label="Email"
            isSelected={!!userNotifications?.optInEmail}
            onClick={() => handleSelect("optInEmail")}
          />
          <QuestionChip
            label="Phone"
            isSelected={!!userNotifications?.optInPhone}
            onClick={() => handleSelect("optInPhone")}
          />
          {userNotifications?.optInPhone && (
            <TextField
              value={normalizePhone(profile?.phoneNumber || "")}
              onChange={handleChangePhone}
              name="phoneNumber"
              required
              variant="standard"
              placeholder="Phone number"
            />
          )}
          <LoadingButton
            loading={loading}
            disableElevation
            onClick={handleSave}
          >
            Save
          </LoadingButton>
        </Grid>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 3,
          p: 2.5,
          borderRadius: 2,
          border: "1px solid",
          borderColor: doNotEmail ? "error.light" : "divider",
          bgcolor: doNotEmail ? "#fff5f5" : "grey.50",
          maxWidth: 520,
          transition: "all 0.2s ease",
          mb: 3,
        }}
      >
        <Box>
          <Typography fontWeight={600} sx={{ mb: 0.5 }}>
            Do not email me
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Turn this on to stop receiving all automated emails from us —
            including tips, product updates, and outreach reminders. You will no
            longer hear from us unless you reach out directly.
          </Typography>
        </Box>
        {doNotEmailLoading ? (
          <CircularProgress size={50} />
        ) : (
          <Switch
            checked={doNotEmail}
            onChange={(e) => handleDoNotEmailToggle(e.target.checked)}
            color="error"
            sx={{ flexShrink: 0, mt: 0.25 }}
          />
        )}
      </Box>
    </>
  );
}
